/**
 * "Rregullimet e pushimeve" — manual add/remove of leave days.
 *
 * An append-only ledger. There is no edit and no delete, here or in the API:
 * a finished adjustment is corrected by posting a REVERSAL, which inserts the
 * opposite entry and links the two so both stay visible.
 *
 * The before/delta/after preview and every guard (mandatory reason, step size,
 * negative-balance rule) are the backend's — GET /api/leave-adjustments/preview
 * returns exactly the numbers POST will store. The form mirrors the client-side
 * checks only to keep the button honest; the server is what enforces them.
 *
 * Two permissions: `leave_adjustments.view` reads the ledger,
 * `leave_adjustments.manage` may move a balance.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Button, TextField, MenuItem, Select,
  FormControl, InputLabel, InputAdornment, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, TablePagination, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, AlertTitle, Skeleton, Tooltip, IconButton,
  Divider, ToggleButton, ToggleButtonGroup, Snackbar, LinearProgress, Tabs, Tab,
} from '@mui/material';
import AddIcon             from '@mui/icons-material/Add';
import RemoveIcon          from '@mui/icons-material/Remove';
import SearchIcon          from '@mui/icons-material/Search';
import UndoRoundedIcon     from '@mui/icons-material/UndoRounded';
import VisibilityIcon      from '@mui/icons-material/VisibilityOutlined';
import TuneRoundedIcon     from '@mui/icons-material/TuneRounded';
import ArrowForwardIcon    from '@mui/icons-material/ArrowForward';
import RefreshRoundedIcon  from '@mui/icons-material/RefreshRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import DownloadRoundedIcon   from '@mui/icons-material/DownloadRounded';
import apiService, { authFetch } from '../services/apiServices';
import usePermissions from '../hooks/usePermissions';
import PERMISSIONS from '../config/permissions/permissionsConfig';
import PageHeader from '../components/common/PageHeader';
import WorkerPicker from '../components/common/WorkerPicker';
import AuditTrailPanel from '../components/leave/LeaveAdjustmentAudit';
import StatusChip from '../components/common/StatusChip';

/** Mirrors backend/services/leaveAdjustment.js — keep the step in sync. */
export const DAY_STEP = 0.25;
export const MIN_REASON_LENGTH = 5;

/* ── Attachment rules — mirror backend/server.js `leaveAdjDocUpload` ──────────
 * The server re-checks both. These exist so the dialog can say no before a
 * 5 MB upload travels, not so the browser can decide. */
export const ATTACH_MAX_BYTES = 5 * 1024 * 1024;
export const ATTACH_MIMES = ['application/pdf', 'image/jpeg', 'image/png'];

export function attachmentError(file) {
  if (!file) return '';
  if (!ATTACH_MIMES.includes(file.type)) return 'Lejohen vetëm PDF, JPG ose PNG.';
  if (file.size > ATTACH_MAX_BYTES) return 'Skedari kalon 5 MB.';
  return '';
}

const fmtBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Opens the evidence behind the same permission that lists the ledger. */
export async function openAdjustmentAttachment(id, onError) {
  try {
    const res = await authFetch(`/api/leave-adjustments/${id}/attachment`);
    if (!res.ok) { onError?.('Bashkëngjitja nuk u hap.'); return; }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    // Revoked late: revoking immediately races the new tab's own load.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { onError?.('Bashkëngjitja nuk u hap.'); }
}

const fmtDays = (n) => {
  const r = Math.round((Number(n) || 0) * 100) / 100;
  return r.toFixed(2).replace(/\.?0+$/, '');
};
export const fmtSignedDays = (n) => ((Number(n) || 0) > 0 ? `+${fmtDays(n)}` : fmtDays(n));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const YEARS = (() => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2, y - 3]; })();

/**
 * Client-side mirror of the server's validation. Returns '' when the form may
 * be submitted. The server re-checks everything — this only stops a round trip.
 */
export function formError({ workerId, leaveTypeId, days, reason }) {
  if (!workerId)    return 'Zgjidhni punonjësin.';
  if (!leaveTypeId) return 'Zgjidhni llojin e pushimit.';
  const n = Number(String(days).replace(',', '.'));
  if (days === '' || days === null || days === undefined) return 'Numri i ditëve mungon.';
  if (!Number.isFinite(n)) return 'Numri i ditëve nuk është vlerë e vlefshme.';
  if (n <= 0)              return 'Numri i ditëve duhet të jetë më i madh se zero.';
  if (Math.round(n * 100) % Math.round(DAY_STEP * 100) !== 0) return 'Ditët lejohen me hap prej 0.25 (çerek dite).';
  if (!String(reason || '').trim())                          return 'Arsyeja e ndryshimit është e detyrueshme.';
  if (String(reason).trim().length < MIN_REASON_LENGTH)      return `Arsyeja duhet të ketë së paku ${MIN_REASON_LENGTH} karaktere.`;
  return '';
}

export default function LeaveAdjustmentsPage() {
  const { can } = usePermissions();
  const canView   = can(PERMISSIONS.LEAVE_ADJUSTMENTS_VIEW) || can(PERMISSIONS.LEAVE_ADJUSTMENTS_MANAGE);
  const canManage = can(PERMISSIONS.LEAVE_ADJUSTMENTS_MANAGE);

  const [workers, setWorkers]     = useState([]);
  const [leaveTypes, setTypes]    = useState([]);
  const [departments, setDepts]   = useState([]);

  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState('');

  const [fWorker, setFWorker] = useState('');
  const [fDept, setFDept]     = useState('');
  const [fType, setFType]     = useState('');
  const [fYear, setFYear]     = useState(new Date().getFullYear());
  const [fStatus, setFStatus] = useState('');
  const [fAction, setFAction] = useState('');
  const [q, setQ]             = useState('');
  const [page, setPage]       = useState(0);
  const [rpp, setRpp]         = useState(25);

  const [tab, setTab]               = useState('ledger');   // 'ledger' | 'audit'
  const [formOpen, setFormOpen]     = useState(false);
  const [detailId, setDetailId]     = useState(null);
  const [reverseRow, setReverseRow] = useState(null);
  const [toast, setToast]           = useState('');

  useEffect(() => {
    Promise.all([
      apiService().get('selectworker').catch(() => null),
      apiService().get('leaveTypes').catch(() => null),
      apiService().get('departments').catch(() => null),
    ]).then(([w, t, d]) => {
      setWorkers(w?.data || []);
      setTypes(t?.data || []);
      setDepts((d?.data || []).filter((x) => x?.Name || x?.name));
    });
  }, []);

  const load = useCallback(async () => {
    setLoad(true); setError('');
    try {
      const qs = new URLSearchParams({ limit: String(rpp), offset: String(page * rpp) });
      if (fWorker) qs.set('workerId', fWorker);
      if (fDept)   qs.set('departmentId', fDept);
      if (fType)   qs.set('leaveTypeId', fType);
      if (fYear)   qs.set('year', String(fYear));
      if (fStatus) qs.set('status', fStatus);
      if (fAction) qs.set('action', fAction);
      if (q.trim()) qs.set('q', q.trim());

      const res  = await authFetch(`/api/leave-adjustments?${qs}`);
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) { setError('Nuk keni leje për të parë rregullimet e pushimeve.'); setRows([]); setTotal(0); return; }
      if (!res.ok || json.success === false) { setError(json.error || `Nuk u morën të dhënat (HTTP ${res.status}).`); setRows([]); setTotal(0); return; }
      setRows(json.data?.rows || []);
      setTotal(json.data?.total || 0);
    } catch {
      setError('Nuk u lidh dot me serverin. Kontrollo lidhjen dhe provo përsëri.');
      setRows([]); setTotal(0);
    } finally { setLoad(false); }
  }, [rpp, page, fWorker, fDept, fType, fYear, fStatus, fAction, q]);

  useEffect(() => { if (canView) load(); }, [load, canView]);

  if (!canView) {
    return (
      <Box>
        <PageHeader title="Rregullimet e pushimeve" />
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Qasje e kufizuar</AlertTitle>
          Nuk keni leje për të parë këtë modul.
        </Alert>
      </Box>
    );
  }

  const thSx = { fontWeight: 700, fontSize: '0.68rem', py: 1, px: 1.25, color: '#6B7280', bgcolor: '#F9FAFB', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' };
  const tdSx = { py: 0.75, px: 1.25, fontSize: '0.8rem', borderBottom: '1px solid #F3F4F6' };

  return (
    <Box>
      <PageHeader title="Rregullimet e pushimeve" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5, minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem' } }}>
        <Tab value="ledger" label="Rregullimet" />
        <Tab value="audit"  label="Historiku i auditimit" />
      </Tabs>

      {tab === 'audit' && <AuditTrailPanel workers={workers} />}

      {tab === 'ledger' && (
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
        {/* ── Filters ──
             Gjerësitë janë të SHPREHURA, jo `minWidth`. Një `<FormControl>` në
             një rresht flex e merr gjerësinë nga përmbajtja e vet, dhe një
             `<Select>` bosh nuk ka përmbajtje — ndaj rreshti shpërbëhej në
             fusha sa tërë gjerësia, një për rresht. Me gjerësi të caktuar
             toolbar-i mbetet toolbar, dhe `flexWrap` e mbështjell vetëm kur
             ekrani vërtet ngushtohet. */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
          <FormControl size="small" sx={{ width: 112, flexShrink: 0 }}>
            <InputLabel>Viti</InputLabel>
            <Select label="Viti" value={fYear} onChange={(e) => { setFYear(e.target.value); setPage(0); }}>
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <WorkerPicker
            workers={workers} value={fWorker} emptyLabel="Të gjithë"
            onChange={(v) => { setFWorker(v); setPage(0); }}
            fullWidth={false} sx={{ width: 226, flexShrink: 0 }}
          />
          <FormControl size="small" sx={{ width: 168, flexShrink: 0 }}>
            <InputLabel>Departamenti</InputLabel>
            <Select label="Departamenti" value={fDept} onChange={(e) => { setFDept(e.target.value); setPage(0); }}>
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              {departments.map((d) => <MenuItem key={d.Id ?? d.id} value={d.Id ?? d.id}>{d.Name || d.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 168, flexShrink: 0 }}>
            <InputLabel>Lloji</InputLabel>
            <Select label="Lloji" value={fType} onChange={(e) => { setFType(e.target.value); setPage(0); }}>
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              {leaveTypes.map((t) => <MenuItem key={t.Id ?? t.id} value={t.Id ?? t.id}>{t.Name || t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 140, flexShrink: 0 }}>
            <InputLabel>Statusi</InputLabel>
            <Select label="Statusi" value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPage(0); }}>
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              <MenuItem value="applied">Në fuqi</MenuItem>
              <MenuItem value="reversed">Të kthyera</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup size="small" exclusive value={fAction}
            onChange={(_, v) => { setFAction(v || ''); setPage(0); }}>
            <ToggleButton value="add" sx={{ textTransform: 'none', px: 1.25 }}>Shtim</ToggleButton>
            <ToggleButton value="subtract" sx={{ textTransform: 'none', px: 1.25 }}>Zbritje</ToggleButton>
          </ToggleButtonGroup>

          <TextField size="small" placeholder="Kërko arsye, referencë, punonjës…"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
            sx={{ flex: '1 1 200px', minWidth: 170 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /></InputAdornment> }} />

          <Tooltip title="Rifresko">
            <span>
              <IconButton size="small" onClick={load} disabled={loading}
                sx={{ border: '1px solid #E5E7EB', borderRadius: 1.5, width: 34, height: 34 }}>
                <RefreshRoundedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>

          {canManage && (
            <Button variant="contained" size="small" startIcon={<TuneRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setFormOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Rregullim i ri
            </Button>
          )}
        </Box>

        {loading && <LinearProgress />}

        {!loading && error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}
              action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Provo përsëri</Button>}>
              {error}
            </Alert>
          </Box>
        )}

        {/* ── Ledger ── */}
        {!error && (
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Punonjësi', 'Lloji', 'Viti', 'Ndryshimi', 'Para', 'Pas', 'Arsyeja', 'Referenca', 'Dëshmia', 'Statusi', 'Nga', 'Krijuar', ''].map((h) => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>{[...Array(13)].map((__, j) => <TableCell key={j} sx={tdSx}><Skeleton height={16} /></TableCell>)}</TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.disabled">Nuk ka rregullime për filtrat e zgjedhur</Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => {
                  const isReversed = r.Status === 'reversed';
                  const isReversal = !!r.ReversalOf;
                  return (
                    <TableRow key={r.Id} hover sx={{ opacity: isReversed ? 0.6 : 1 }}>
                      <TableCell sx={tdSx}>{r.WorkerName || '—'}</TableCell>
                      <TableCell sx={tdSx}>{r.LeaveTypeName || r.LeaveTypeId}</TableCell>
                      <TableCell sx={tdSx}>{r.Year}</TableCell>
                      <TableCell sx={{ ...tdSx, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: r.Days < 0 ? '#B91C1C' : '#16A34A', textDecoration: isReversed ? 'line-through' : 'none' }}>
                        {fmtSignedDays(r.Days)} d
                      </TableCell>
                      <TableCell sx={{ ...tdSx, fontVariantNumeric: 'tabular-nums', color: '#6B7280' }}>{fmtDays(r.BalanceBefore)}</TableCell>
                      <TableCell sx={{ ...tdSx, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtDays(r.BalanceAfter)}</TableCell>
                      <TableCell sx={{ ...tdSx, maxWidth: 220 }}>
                        <Tooltip title={r.Reason}><Typography sx={{ fontSize: '0.78rem' }} noWrap>{r.Reason}</Typography></Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>{r.Reference || '—'}</TableCell>
                      <TableCell sx={tdSx}>
                        {r.HasAttachment ? (
                          <Tooltip title={r.AttachmentName || 'Hap dëshminë'}>
                            <IconButton size="small" sx={{ width: 26, height: 26, color: '#0369A1' }}
                              onClick={() => openAdjustmentAttachment(r.Id, setError)}>
                              <AttachFileRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        ) : <Typography sx={{ fontSize: '0.78rem', color: '#D1D5DB' }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        {/* Fjalori i statuseve jeton te `StatusChip`, jo këtu.
                            Më parë ishin tri pilula të shkruara me dorë me gjashtë
                            hex-e — dhe "E kthyer" ishte e kuqe, sikur të ishte
                            gabim. Një rregullim i kthyer nuk është dështim: është
                            korrigjim i kryer, ndaj rri neutral. E kuqja i mbetet
                            asaj që vërtet ka shkuar keq. */}
                        <StatusChip
                          size="sm"
                          status={isReversed ? 'reversed' : isReversal ? 'reversal' : 'applied'}
                        />
                      </TableCell>
                      <TableCell sx={tdSx}>{r.CreatedBy || '—'}</TableCell>
                      <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap', color: '#6B7280' }}>{fmtDateTime(r.CreatedAt)}</TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Tooltip title="Detajet">
                          <IconButton size="small" onClick={() => setDetailId(r.Id)} sx={{ width: 26, height: 26 }}>
                            <VisibilityIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        {/* No edit, no delete — by design. Correction = reversal. */}
                        {canManage && !isReversed && !isReversal && (
                          <Tooltip title="Kthe me transaksion kundërt">
                            <IconButton size="small" onClick={() => setReverseRow(r)} sx={{ width: 26, height: 26, color: '#B45309' }}>
                              <UndoRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!error && (
          <TablePagination
            component="div" count={total} page={page} rowsPerPage={rpp}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRpp(+e.target.value); setPage(0); }}
            labelRowsPerPage="Rreshta:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            sx={{ borderTop: '1px solid #F3F4F6', fontSize: '0.75rem' }}
          />
        )}
      </Paper>
      )}

      {canManage && (
        <AdjustmentForm
          open={formOpen} onClose={() => setFormOpen(false)}
          workers={workers} leaveTypes={leaveTypes}
          onSaved={(msg) => { setFormOpen(false); setToast(msg); load(); }}
        />
      )}
      <DetailDialog id={detailId} onClose={() => setDetailId(null)} />
      {canManage && (
        <ReverseDialog
          row={reverseRow} onClose={() => setReverseRow(null)}
          onDone={(msg) => { setReverseRow(null); setToast(msg); load(); }}
        />
      )}

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

/* ─── New adjustment ──────────────────────────────────────────────────────── */
function AdjustmentForm({ open, onClose, workers, leaveTypes, onSaved }) {
  const [workerId, setWorkerId]   = useState('');
  const [leaveTypeId, setTypeId]  = useState('');
  const [year, setYear]           = useState(new Date().getFullYear());
  const [action, setAction]       = useState('add');
  const [days, setDays]           = useState('');
  const [reason, setReason]       = useState('');
  const [effectiveDate, setEff]   = useState('');
  const [reference, setRef]       = useState('');
  const [attachment, setAttach]   = useState(null);

  const [preview, setPreview]     = useState(null);
  const [previewing, setPrev]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [serverError, setSrvErr]  = useState('');

  useEffect(() => {
    if (!open) return;
    setWorkerId(''); setTypeId(''); setYear(new Date().getFullYear());
    setAction('add'); setDays(''); setReason(''); setEff(''); setRef('');
    setAttach(null); setPreview(null); setSrvErr('');
  }, [open]);

  const clientError = useMemo(
    () => formError({ workerId, leaveTypeId, days, reason }),
    [workerId, leaveTypeId, days, reason]
  );
  const attachError = useMemo(() => attachmentError(attachment), [attachment]);

  // The preview is the SERVER's arithmetic — the dialog never computes the
  // resulting balance itself, so what is shown is what will be stored.
  useEffect(() => {
    if (!open || !workerId || !leaveTypeId) { setPreview(null); return; }
    let alive = true;
    setPrev(true);
    const qs = new URLSearchParams({ workerId, leaveTypeId, year: String(year), action, days: String(days || 0) });
    authFetch(`/api/leave-adjustments/preview?${qs}`)
      .then((r) => r.json())
      .then((j) => { if (alive) setPreview(j?.data || null); })
      .catch(() => { if (alive) setPreview(null); })
      .finally(() => { if (alive) setPrev(false); });
    return () => { alive = false; };
  }, [open, workerId, leaveTypeId, year, action, days]);

  const wouldGoNegative = preview && !preview.allowNegative && preview.after < 0;

  const submit = async () => {
    setSaving(true); setSrvErr('');
    try {
      const fields = {
        WorkerId: workerId, LeaveTypeId: leaveTypeId, Year: year,
        Action: action, Days: days, Reason: reason,
        EffectiveDate: effectiveDate || null, Reference: reference || null,
      };

      // The file rides in on the SAME request as the adjustment. The ledger is
      // append-only, so a follow-up upload would mean a row that changed after
      // it was written — the server refuses to work that way, and so does this.
      // With no file, the body stays exactly the JSON it has always been.
      let res;
      if (attachment) {
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => { if (v !== null) fd.append(k, String(v)); });
        fd.append('attachment', attachment);
        // No Content-Type header: the browser must set the multipart boundary.
        res = await authFetch('/api/leave-adjustments', { method: 'POST', body: fd });
      } else {
        res = await authFetch('/api/leave-adjustments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fields),
        });
      }
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) { setSrvErr('Nuk keni leje për të bërë rregullime.'); return; }
      if (!res.ok || json.success === false) { setSrvErr(json.error || `Gabim (HTTP ${res.status}).`); return; }
      onSaved('✓ Rregullimi u ruajt.');
    } catch {
      setSrvErr('Nuk u lidh dot me serverin.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Rregullim i ri i pushimeve</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={1.75}>
          <WorkerPicker
            workers={workers} value={workerId} onChange={setWorkerId}
            label="Punonjësi" required
          />

          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Lloji i pushimit *</InputLabel>
              <Select label="Lloji i pushimit *" value={leaveTypeId} onChange={(e) => setTypeId(e.target.value)}>
                {leaveTypes.map((t) => <MenuItem key={t.Id ?? t.id} value={t.Id ?? t.id}>{t.Name || t.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Viti *</InputLabel>
              <Select label="Viti *" value={year} onChange={(e) => setYear(e.target.value)}>
                {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <ToggleButtonGroup size="small" exclusive value={action}
              onChange={(_, v) => v && setAction(v)} sx={{ height: 40 }}>
              <ToggleButton value="add" sx={{ textTransform: 'none', px: 1.5, '&.Mui-selected': { bgcolor: '#DCFCE7', color: '#15803D' } }}>
                <AddIcon sx={{ fontSize: 16, mr: 0.5 }} /> Shto ditë
              </ToggleButton>
              <ToggleButton value="subtract" sx={{ textTransform: 'none', px: 1.5, '&.Mui-selected': { bgcolor: '#FEE2E2', color: '#B91C1C' } }}>
                <RemoveIcon sx={{ fontSize: 16, mr: 0.5 }} /> Zbrit ditë
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField size="small" type="number" label="Ditët *" value={days}
              onChange={(e) => setDays(e.target.value)}
              inputProps={{ step: DAY_STEP, min: 0 }}
              sx={{ width: 150 }}
              helperText={`Hap ${DAY_STEP} ditë`}
              InputProps={{ endAdornment: <InputAdornment position="end">d</InputAdornment> }} />
          </Stack>

          <TextField size="small" fullWidth multiline minRows={2} label="Arsyeja *"
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Pse po ndryshohet balanca…"
            helperText={`E detyrueshme — së paku ${MIN_REASON_LENGTH} karaktere`} />

          <Stack direction="row" spacing={1.5}>
            <TextField size="small" fullWidth type="date" label="Data efektive"
              value={effectiveDate} onChange={(e) => setEff(e.target.value)}
              InputLabelProps={{ shrink: true }} helperText="Opsionale" />
            <TextField size="small" fullWidth label="Referenca"
              value={reference} onChange={(e) => setRef(e.target.value)}
              placeholder="Nr. vendimi, ticket…" helperText="Opsionale" />
          </Stack>

          {/* Dëshmia — vendimi që e justifikon ndryshimin.
              Lidhet me KËTË rregullim, jo me dosjen e punonjësit: dosja do të
              thoshte vetëm se dokumenti ekziston diku. */}
          <Box>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
              Bashkëngjitja
            </Typography>
            {!attachment ? (
              <Button component="label" variant="outlined" size="small"
                startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontWeight: 600 }}>
                Zgjidh dokumentin
                <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => { setAttach(e.target.files?.[0] || null); e.target.value = ''; }} />
              </Button>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center"
                sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: '#FAFBFC', border: '1px solid #EEF0F3' }}>
                <AttachFileRoundedIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                  {attachment.name}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{fmtBytes(attachment.size)}</Typography>
                <Button size="small" onClick={() => setAttach(null)} sx={{ textTransform: 'none', minWidth: 0 }}>Hiq</Button>
              </Stack>
            )}
            <Typography sx={{ fontSize: '0.7rem', color: attachError ? '#B91C1C' : '#9CA3AF', mt: 0.5 }}>
              {attachError || 'Opsionale — PDF, JPG ose PNG deri në 5 MB. Ruhet bashkë me rregullimin dhe nuk ndryshohet më pas.'}
            </Typography>
          </Box>

          {/* ── Preview: current / change / after ── */}
          <PreviewPanel preview={preview} loading={previewing} />

          {wouldGoNegative && (
            <Alert severity="error" sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}>
              Zbritja e kalon balancën e disponueshme. Balanca negative nuk lejohet
              nga konfigurimi aktual.
            </Alert>
          )}
          {serverError && <Alert severity="error" sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}>{serverError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {clientError && <Typography variant="caption" color="error" sx={{ flex: 1 }}>{clientError}</Typography>}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" onClick={submit}
          disabled={saving || !!clientError || !!attachError || wouldGoNegative}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? 'Duke ruajtur…' : 'Konfirmo rregullimin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PreviewPanel({ preview, loading }) {
  const cell = (label, value, color) => (
    <Box sx={{ flex: 1, textAlign: 'center', px: 1, py: 1.25, borderRadius: 1.5, bgcolor: '#FAFBFC', border: '1px solid #EEF0F3' }}>
      {loading
        ? <Skeleton width={50} height={26} sx={{ mx: 'auto' }} />
        : <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{value}</Typography>}
      <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.4 }}>{label}</Typography>
    </Box>
  );
  const p = preview || {};
  return (
    <Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
        Para konfirmimit
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        {cell('Balanca aktuale', preview ? fmtDays(p.before) : '—', '#374151')}
        <ArrowForwardIcon sx={{ fontSize: 16, color: '#D1D5DB' }} />
        {cell('Ndryshimi', preview ? fmtSignedDays(p.delta) : '—', (p.delta || 0) < 0 ? '#B91C1C' : '#16A34A')}
        <ArrowForwardIcon sx={{ fontSize: 16, color: '#D1D5DB' }} />
        {cell('Balanca pas', preview ? fmtDays(p.after) : '—', (p.after || 0) < 0 ? '#B91C1C' : '#0369A1')}
      </Stack>
    </Box>
  );
}

/* ─── Detail ──────────────────────────────────────────────────────────────── */
function DetailDialog({ id, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) { setData(null); return; }
    let alive = true;
    setLoading(true);
    authFetch(`/api/leave-adjustments/${id}`)
      .then((r) => r.json())
      .then((j) => { if (alive) setData(j?.data || null); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const field = (label, value) => (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.6, borderBottom: '1px solid #F3F4F6' }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value ?? '—'}</Typography>
    </Stack>
  );

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Detajet e rregullimit</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack spacing={1}>{[...Array(6)].map((_, i) => <Skeleton key={i} height={26} />)}</Stack>
        ) : !data ? (
          <Typography sx={{ py: 3, textAlign: 'center', color: '#94A3B8', fontSize: '0.85rem' }}>Nuk u gjet.</Typography>
        ) : (
          <Stack spacing={0.25}>
            {field('Punonjësi', data.WorkerName)}
            {field('Lloji', data.LeaveTypeName)}
            {field('Viti', data.Year)}
            {field('Ndryshimi', `${fmtSignedDays(data.Days)} ditë`)}
            {field('Balanca para', fmtDays(data.BalanceBefore))}
            {field('Balanca pas', fmtDays(data.BalanceAfter))}
            {field('Arsyeja', data.Reason)}
            {field('Data efektive', fmtDate(data.EffectiveDate))}
            {field('Referenca', data.Reference)}
            {field('Dëshmia', data.HasAttachment ? (
              <Button size="small" startIcon={<DownloadRoundedIcon sx={{ fontSize: 15 }} />}
                onClick={() => openAdjustmentAttachment(data.Id)}
                sx={{ textTransform: 'none', fontWeight: 700, p: 0, minWidth: 0, fontSize: '0.78rem' }}>
                {data.AttachmentName || 'Hap dokumentin'}
              </Button>
            ) : 'Pa bashkëngjitje')}
            {field('Statusi', (
              <StatusChip size="sm" status={data.Status === 'reversed' ? 'reversed' : (data.ReversalOf ? 'reversal' : 'applied')} />
            ))}
            {field('Nga', data.CreatedBy)}
            {field('Krijuar', fmtDateTime(data.CreatedAt))}

            {data.Linked && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'info.main', textTransform: 'uppercase', mb: 0.5 }}>
                  {data.ReversalOf ? 'Kthen rregullimin' : 'U kthye nga rregullimi'} #{data.Linked.Id}
                </Typography>
                {field('Ndryshimi', `${fmtSignedDays(data.Linked.Days)} ditë`)}
                {field('Arsyeja', data.Linked.Reason)}
                {field('Nga', data.Linked.CreatedBy)}
              </>
            )}

            {Array.isArray(data.History) && data.History.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', mb: 0.5 }}>
                  Audit
                </Typography>
                {data.History.map((h) => (
                  <Stack key={h.Id} direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
                    <Typography sx={{ fontSize: '0.75rem' }}>{h.Action} · {h.UserEmail}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{fmtDateTime(h.CreatedAt)}</Typography>
                  </Stack>
                ))}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Mbyll</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── Reversal ────────────────────────────────────────────────────────────── */
function ReverseDialog({ row, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => { setReason(''); setError(''); }, [row]);
  if (!row) return null;

  const tooShort = reason.trim().length < MIN_REASON_LENGTH;

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const res = await authFetch(`/api/leave-adjustments/${row.Id}/reverse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Reason: reason }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) { setError('Nuk keni leje për ta kthyer këtë rregullim.'); return; }
      if (!res.ok || json.success === false) { setError(json.error || `Gabim (HTTP ${res.status}).`); return; }
      onDone('✓ U krijua transaksioni kundërt.');
    } catch {
      setError('Nuk u lidh dot me serverin.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Kthe rregullimin</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem', borderRadius: 1.5 }}>
          Rregullimi origjinal nuk fshihet dhe nuk ndryshohet. Krijohet një
          transaksion i ri kundërt prej <b>{fmtSignedDays(-row.Days)} ditë</b>,
          i lidhur me të.
        </Alert>
        <TextField size="small" fullWidth multiline minRows={2} autoFocus
          label="Arsyeja e kthimit *" value={reason} onChange={(e) => setReason(e.target.value)}
          helperText={`E detyrueshme — së paku ${MIN_REASON_LENGTH} karaktere`} />
        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.8rem', borderRadius: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" color="warning" onClick={submit} disabled={saving || tooShort}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? 'Duke kthyer…' : 'Krijo transaksionin kundërt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
